import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import { useForm, Controller } from 'react-hook-form'
import LayoutSidebar from '../../../components/LayoutSidebar'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons'
import { selectAuth } from '../../../redux/authSlice'
import { selectPortfolios } from '../../../redux/portfolioSlice'

export default function LeadBuyManagement() {
  const request = useRequest()
  const dispatch = useDispatch()
  const { register, handleSubmit, control, watch, reset } = useForm();
  const auth = useSelector(selectAuth);
  const portfolios = useSelector(selectPortfolios);
  const [filteredPortfolios, setFilteredPortfolios] = useState([]);
  const [responseData, setResponseData] = useState([])
  const [totals, setTotals] = useState([]); // Add this to keep track of totals
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!auth.fromPageChange) { return }
    if (auth.accessToken) {
      getLeadByManagement()
    }
  }, [auth.accessToken])

  useEffect(() => {
    if (portfolios.data) {
      setFilteredPortfolios(portfolios.data.filter((e) => e.is_active === 1 && e.code_key !== 'DEV'))
    }
  }, [portfolios.data])

  const getLeadByManagement = async () => {
    try {
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/lead_buy_management_info`)
      const response = await request.get()
      setResponseData(response?.data?.data);
    } catch (error) {
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
    }
  }

  useEffect(() => {
    computeTotals();
  }, [responseData])

  const computeTotals = () => {
    const newTotals = responseData?.providers?.map((provider, providerIndex) => {
        return filteredPortfolios.reduce((sum, portfolio) => {
          const split = responseData?.splits?.find(
            (split) => split.lead_provider_id === provider.id && split.portfolio_id === portfolio.id
          );
          const splitPercentage = split ? split.split_percentage : 0;
          return sum + parseFloat(splitPercentage);
        }, 0)
    })
    setTotals(newTotals);
  }

  const onSubmit = async (formData) => {
    setSaving(true)
    if (totals.some((value) => value !== 100)) {
      setSaving(false)
      dispatch(setNotification({ type: 'error', body: 'Sum of split percentage should be 100 for each portfolio.' }))
      return;
    }
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/lead_buy_management`)
    try {
      const response = await request.post(formData)
      if (response.status === 200 || response.status === 201 || response.status === 204) {
        setSaving(false)
        dispatch(setNotification({ type: 'success', body: '' }))
        getLeadByManagement()
      } else {
        setSaving(false)
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      setSaving(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  };

  useEffect(() => {
    const unsub = watch((values) => {
      setPortfolioSplitSum(values);
    });
  }, [watch, responseData]);

  // Update the totals whenever any input field value changes
  const setPortfolioSplitSum = (values) => { // Add the event parameter
    const newTotals = responseData?.providers?.map((provider, providerIndex) => {
      return filteredPortfolios.reduce((sum, portfolio, portfolioIndex) => {
        const value = parseFloat(values?.split_percentages[providerIndex][portfolioIndex] || 0); // Use getValues to fetch the value
        return sum + value;
      }, 0)
    })

    setTotals(newTotals);
  };

  const handleCancel = () => {
    const defaultValues = {
      split_percentages: responseData.providers?.map((provider) => {
        return filteredPortfolios.reduce((values, portfolio, portfolioIndex) => {
          const split = responseData.splits?.find(
            (split) => split.lead_provider_id === provider.id && split.portfolio_id === portfolio.id
          );
          const splitPercentage = split ? split.split_percentage : 0;
          return {
            ...values,
            [portfolioIndex]: splitPercentage
          };
        }, {})
      })
    };
    reset(defaultValues);
  }

  return (
    <LayoutSidebar
      contentTitle={'Lead Buy Management'}
      content={(
        <div className="lox-detail mt-1 mx-1">
          <div className="columns mb-0">
            <div className="column is-flex is-flex-direction-column is-justify-content-center is-align-items-flex-start">
              <div className="title is-4 has-text-primary is-flex">Lead Buy % Split</div>
              <p className="subtitle is-6">INSTRUCTIONS: Fill in the split % for each portfolio for each provider. Each provider&apos;s split % must be equal 100%.</p>
            </div>
          </div>

          {responseData.length !== 0 && (
            <form onSubmit={handleSubmit(onSubmit)} id="lead_buy_management_form">
              <div className="table-container mb-0">
                <table className="lox-hooktable-table table is-bordered is-striped is-narrow is-fullwidth is-hoverable mb-3">
                  <thead>
                    <tr>
                      <th className="is-selected has-text-centered"></th>
                      {responseData?.providers?.map((provider) => (
                        <th className="is-selected has-text-centered" style={{ minWidth: "100px" }} key={provider.id}>{provider.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                  {filteredPortfolios.map((portfolio, portfolioIndex) => (
                    <tr key={portfolio.id}>
                      <td className="has-text-centered">{portfolio.name}</td>
                      {responseData?.providers?.map((provider, providerIndex) => {
                        // Find the corresponding split object
                        const split = responseData?.splits?.find(
                          (split) => split.lead_provider_id === provider.id && split.portfolio_id === portfolio.id,
                        )

                        // Extract the split_percentage value or set it to empty string if no split object is found
                        const splitPercentage = split ? split.split_percentage : 0
                        const inputName = `[${providerIndex}][${portfolioIndex}]`

                        return (
                          <td className="has-text-centered" key={portfolio.id}>
                            <Controller
                              name={`split_percentages${inputName}`}
                              control={control}
                              defaultValue={splitPercentage}
                              render={({ field: { onChange } }) => (
                                <input
                                  {...register(`split_percentages${inputName}`)}
                                  name={`split_percentages${inputName}`}
                                  className="input is-small"
                                  type="number"
                                  form="lead_buy_management_form"
                                  min={0}
                                  max={100}
                                  step="1"
                                />
                              )}
                            />
                            <input
                              type="hidden"
                              name={`providers[${providerIndex}]`}
                              value={provider.id}
                              {...register(`providers[${providerIndex}]`)}
                            />
                            <input
                              type="hidden"
                              name={`portfolios[${portfolioIndex}]`}
                              value={portfolio.id}
                              {...register(`portfolios[${portfolioIndex}]`)}
                            />
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                  </tbody>
                  <thead>
                  <tr>
                    <th className="is-selected has-text-centered has-background-grey-special">TOTALS</th>
                    {totals?.length > 0 && totals.map((total, index) => (
                      <th
                        className={`is-selected has-text-centered ${total != 100 ? 'has-background-danger' : 'has-background-grey-special'}`}
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                      >
                        {total}%
                      </th>
                    ))}
                  </tr>
                  </thead>
                </table>
              </div>

              <div className="columns mb-0">
                <div
                  className="column is-flex is-flex-direction-column is-justify-content-center is-align-items-flex-start"
                >
                  <div className="title is-4 has-text-primary is-flex">Lead Buy Cap Settings</div>
                  <p className="subtitle is-6">INSTRUCTIONS: Fill in the caps you want to see for each portfolio. If
                    empty/blank, there is no cap.</p>
                </div>
              </div>

              <div className="table-container mb-0">
                <table className="lox-hooktable-table table is-bordered is-striped is-narrow is-hoverable mb-3">
                  <thead>
                  <tr>
                    <th className="is-selected has-text-centered"></th>
                    <th className="is-selected has-text-centered">Soft Cap <span
                      className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary'}
                      data-tooltip={'After we hit this we stop buying new customers, and only buy reacts - organics not included in count'}
                    >
                        <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                      </span></th>
                    <th className="is-selected has-text-centered">Hard Cap <span
                      className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary'}
                      data-tooltip={'After we hit this we stop buying all new & react customers - organics not included in count'}
                    >
                        <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                      </span></th>
                  </tr>
                  </thead>
                  <tbody>
                    {filteredPortfolios.map((portfolio, index) => (
                      <tr key={portfolio.id}>
                        <td className="has-text-centered">{portfolio.name}</td>
                        <td className="has-text-centered">
                          <Controller
                            name={`lead_buy_cap_soft[${index}]`}
                            control={control}
                            defaultValue={portfolio?.lead_buy_cap_soft ?? 0}
                            render={({ field: { onChange } }) => (
                              <input
                                name={`lead_buy_cap_soft[${index}]`}
                                className="input is-small"
                                type="number"
                                form="lead_buy_management_form"
                                min={0}
                                step="1"
                                onChange={onChange}
                                {...register(`lead_buy_cap_soft[${index}]`)}
                              />
                            )}
                          />
                        </td>
                        <td className="has-text-centered">
                          <Controller
                            name={`lead_buy_cap_hard[${index}]`}
                            control={control}
                            defaultValue={portfolio?.lead_buy_cap_hard ?? 0}
                            render={({ field: { onChange } }) => (
                              <input
                                name={`lead_buy_cap_hard[${index}]`}
                                className="input is-small"
                                type="number"
                                form="lead_buy_management_form"
                                min={0}
                                step="1"
                                onChange={onChange}
                                {...register(`lead_buy_cap_hard[${index}]`)}
                              />
                            )}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="lox-form__buttons-wrapper has-text-right">
                <button
                  type="button"
                  className="button has-background-grey has-text-white is-small mr-1"
                  onClick={handleCancel}
                >
                  <span className="mx-3">Cancel</span>
                </button>
                <button
                  type="submit"
                  className="button is-link is-small ml-1"
                  disabled={saving && 'disabled'}
                >
                  {saving ? 'Saving' : 'Save'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    />
  )
}
